import { useEffect } from 'react';
import {
  BroadcastOperation,
  CompleteServiceExecutionFragment,
  GetServiceExecutionsDocument,
  GetServiceExecutionsQuery,
  ServiceExecutionUpdateFragment,
  useServiceExecutionUpdateSubscription,
} from 'drycut-graphql';
import { useApolloClient } from '@apollo/client';

type Props = {
  slug: string;
  callback?: (broadcast: ServiceExecutionUpdateFragment) => void;
};

export function useServiceExecutionsPubSub({ slug, callback }: Props) {
  const client = useApolloClient();

  const { data } = useServiceExecutionUpdateSubscription({ variables: { slug } });
  const broadcast: ServiceExecutionUpdateFragment | undefined = data?.serviceExecutionUpdate;

  useEffect(() => {
    if (broadcast) {
      const previous = client.readQuery<GetServiceExecutionsQuery>({
        query: GetServiceExecutionsDocument,
      });

      const previousNodes = (previous?.serviceExecutions?.nodes ||
        []) as CompleteServiceExecutionFragment[];

      switch (broadcast.operation) {
        case BroadcastOperation.Add: {
          // The service is already in the array, so we don't need to add it again.
          if (previousNodes.some((node) => node.id === broadcast.serviceExecution.id)) {
            return;
          }

          const nodes = [...previousNodes, broadcast.serviceExecution];

          client.writeQuery({
            query: GetServiceExecutionsDocument,
            data: {
              ...previous,
              serviceExecutions: { ...previous?.serviceExecutions, nodes },
            },
          });
          break;
        }
        case BroadcastOperation.Remove: {
          client.cache.evict({ id: `ServiceExecution:${broadcast.serviceExecution.id}` });
          client.cache.gc();

          break;
        }
        case BroadcastOperation.Update: {
          // For updates we don't need to do anything since Apollo will update the cache
          // automatically.
          break;
        }
      }

      callback?.(broadcast);
    }
  }, [broadcast, client, callback]);
}
